import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ConfigProvider } from "antd";
import { BlueDotIcon, AssignedLocationIcon, AssignedDateIcon, NotResolvedIcon, RedStatusIcon, LeftHeaderIcon, RightHeaderIcon } from "./assets/svgExport";
import { CustomSelectDropdown, StyledHeader } from "./ThirdPartyStyles";

import {
  // ResourcesWrapper,
  default as useApiHook,
} from "./assets/api/UseAxios";

interface DataCardProps {
  icon?: React.ReactNode;
  title?: string;
  value?: string;
  content?: React.ReactNode;
}

const Header = ({ fetchedData, data }: { fetchedData: any; data: any }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  // const dispatch = useDispatch();

  const details = fetchedData?.data?.[0];
  // const { tableDataRerender } = useSelector((state: any) => state.globalRenderSlice);

  const statusAlias = details?.damages_status?.[0]?.damage_status_type?.name;
  const files = data?.data ?? [];

  const filteredOptions = [
    {
      value: "IP",
      label: (
        <div className="select-option">
          <BlueDotIcon /> <span>In Progress</span>
        </div>
      ),
    },
    {
      value: "RS",
      label: (
        <div className="select-option">
          <RedStatusIcon /> <span>Resolved</span>
        </div>
      ),
    },
    {
      value: "NR",
      label: (
        <div className="select-option">
          <NotResolvedIcon /> <span>Not Resolved</span>
        </div>
      ),
    },
  ];

  const statusMap: Record<string, string> = {
    "In Progress": "IP",
    Resolved: "RS",
    "Not Resolved": "NR",
  };

  const reverseStatusMap: Record<string, string> = {
    IP: "In Progress",
    RS: "Resolved",
    NR: "Not Resolved",
  };
  console.log(reverseStatusMap);
  const { fetchData: postStatus } = useApiHook({
    endpoint: `/vehicle_maintenance/update_by_workshop_status_defect/`,
    method: "POST",
  });
  const scrollThumbnails = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 160;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setIsAtStart(scrollLeft <= 0);
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
  };

  useEffect(() => {
    const current = scrollRef.current;
    if (!current) return;

    current.addEventListener("scroll", checkScrollPosition);
    checkScrollPosition();

    return () => {
      current.removeEventListener("scroll", checkScrollPosition);
    };
  }, []);

  const DataCard: React.FC<DataCardProps> = ({ icon, title, value, content }) => (
    <div className="data-card">
      {icon && <div className="icon">{icon}</div>}
      <div className="details">
        {title && <h1>{title}</h1>}
        {value && <h2>{value}</h2>}
        {content}
      </div>
    </div>
  );

  return (
    <StyledHeader>
      <div className="header-wrapper">
        <DataCard icon={<AssignedLocationIcon />} title="Location" value={details?.vehicle_location} />

        <DataCard icon={<AssignedDateIcon />} title="Address & Postcode" value={details?.vehicle_defect_resolution?.[0]?.workshop?.address} />

        <DataCard
          title="Status"
          content={
            <ConfigProvider>
              <CustomSelectDropdown
                value={statusMap[statusAlias] || "IP"}
                optionLabelProp="label"
                options={filteredOptions}
                onChange={(value: string) => {
                  const payload = {
                    status_alias: value,
                    workshop_id: details?.vehicle_defect_resolution?.[0]?.workshop?.id,
                    damage_id: details?.id,
                  };
                  postStatus({ payload });
                  // dispatch(setTableDataRerender(!tableDataRerender));
                }}
              />
            </ConfigProvider>
          }
        />

        <DataCard
          content={
            <ThumbnailCarousel>
              <ScrollButton left disabled={isAtStart} className={isAtStart ? "disabled" : "scrollbtn"} onClick={() => !isAtStart && scrollThumbnails("left")}>
                <LeftHeaderIcon />
              </ScrollButton>

              <ThumbnailRow ref={scrollRef}>
                {files.map((item: any, index: number) => (
                  <ThumbImage key={item.id || index} src={item.url} alt={item.file_name || `File ${index + 1}`} />
                ))}
              </ThumbnailRow>

              <ScrollButton right disabled={isAtEnd} className={isAtEnd ? "disabled" : "scrollbtn"} onClick={() => !isAtEnd && scrollThumbnails("right")}>
                <RightHeaderIcon />
              </ScrollButton>
            </ThumbnailCarousel>
          }
        />
      </div>
    </StyledHeader>
  );
};

export default Header;

/* ======================= STYLES ======================= */
const ThumbnailCarousel = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const ScrollButton = styled.button<{ left?: boolean; right?: boolean }>`
  position: absolute;
  top: 50%;
  ${(props) => (props.left ? "left: -8px" : "right: -8px")};
  transform: translateY(-50%);
  background: white;
  border: 1px solid #ddd;
  border-radius: 50%;
  padding: 3px 6px;
  cursor: pointer;
  z-index: 1;
  transition: opacity 0.2s ease, cursor 0.2s ease;

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  max-width: 280px;
  margin: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ThumbImage = styled.img`
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
`;
